
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  Box, Paper, TextField, MenuItem, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  Stack, Alert, Snackbar, IconButton, Divider, Chip, Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  WarningAmber, Delete as DeleteIcon, Add as AddIcon, Replay as ReplayIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';

import Client from '../Client';
import Config from '../ClientConfig';

dayjs.extend(customParseFormat);

var FALLBACK_DOC_TYPE_IN_ID = 1;
var FALLBACK_DOC_TYPE_OUT_ID = 2;

var TYPES_FALLBACK = [
  { value: FALLBACK_DOC_TYPE_IN_ID,  isNegative: false, label: 'Internal IN' },
  { value: FALLBACK_DOC_TYPE_OUT_ID, isNegative: true,  label: 'Internal OUT' },
];

var QUANTITY_INPUT_PROPS = { step: 'any', inputMode: 'decimal', min: 0 };
var PRICE_INPUT_PROPS = { step: 'any', inputMode: 'decimal' };

function money(n) {
  var x = Number(n);
  if (!isFinite(x)) x = 0;
  return x.toFixed(2);
}

const DRAFT_KEY = 'iit_smart_compact_v2_draft';
const LAST_TXN_KEY = 'iit_last_transaction_v2';

const compactFieldSx = {
  '& .MuiInputBase-root': {
    minHeight: 34,
    borderRadius: '12px',
  },
  '& .MuiInputBase-input': {
    fontSize: {
      xs: '16px',
      sm: '0.84rem',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: {
      xs: '0.78rem',
      sm: '0.84rem',
    },
  },
};

const compactButtonSx = {
  height: 32,
  borderRadius: '11px',
  textTransform: 'none',
  fontWeight: 700,
};

const pageRootSx = {
  width: '100%',
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  minWidth: 0,
  overflow: 'hidden',
  bgcolor: 'background.default',
  p: {
    xs: 0.5,
    sm: 0.85,
    md: 1.1,
  },
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
};

const mainCardSx = {
  width: '100%',
  maxWidth: 980,
  height: '100%',
  maxHeight: '100%',
  minHeight: 0,
  borderRadius: '14px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const compactSaveBarSx = {
  flexShrink: 0,
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  borderTop: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  px: {
    xs: 0.75,
    sm: 1,
  },
  py: 0.75,
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  flexWrap: 'wrap',
  '& .MuiButton-root': {
    height: 34,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 750,
  },
};

class InventoryInternalTransactionsSmartCompactV2 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      client: new Client('none'),
      loading: false,
      saving: false,

      // lookups
      locations: [],
      products: [],
      docTypes: [],
      inId: FALLBACK_DOC_TYPE_IN_ID,
      outId: FALLBACK_DOC_TYPE_OUT_ID,

      // mode
      mode: 'view', // 'view' | 'edit'

      // header
      location: '',
      trxDate: dayjs().format('YYYY-MM-DD'),
      tType: FALLBACK_DOC_TYPE_OUT_ID,

      // lines
      lines: [ { product: '', qty: 1, price: '' } ],

      // last close
      lastCloseByLocation: {},
      warningDialogOpen: false,
      pendingSubmit: null,

      // saved
      lastSaved: null,

      // feedback
      snackOpen: false,
      snackMsg: '',
      snackSeverity: 'success',

      applyErrorOpen: false,
      applyErrorDetail: '',
      productMap: {},
      productCodeMap: {},

      lastDeletedLine: null,
      lastDeletedIndex: -1,
    };

    this.fetchLookups = this.fetchLookups.bind(this);
    this.validDate = this.validDate.bind(this);
    this.isTypeIN = this.isTypeIN.bind(this);
    this.getLastClosedDateForLoc = this.getLastClosedDateForLoc.bind(this);
    this.isBeforeClose = this.isBeforeClose.bind(this);
    this.lineIsValid = this.lineIsValid.bind(this);
    this.canSave = this.canSave.bind(this);
    this.addLine = this.addLine.bind(this);
    this.removeLine = this.removeLine.bind(this);
    this.updateLine = this.updateLine.bind(this);
    this.buildPayload = this.buildPayload.bind(this);
    this.postTransaction = this.postTransaction.bind(this);
    this.onSave = this.onSave.bind(this);
    this.duplicateFromSaved = this.duplicateFromSaved.bind(this);
    this.clearForm = this.clearForm.bind(this);
    this.findLocationName = this.findLocationName.bind(this);
    this.saveDraft = this.saveDraft.bind(this);
    this.restoreDraft = this.restoreDraft.bind(this);
    this.clearDraft = this.clearDraft.bind(this);
    this.switchToEdit = this.switchToEdit.bind(this);
    this.switchToView = this.switchToView.bind(this);
    this.loadLastTxnFromStorage = this.loadLastTxnFromStorage.bind(this);
  }

  componentDidMount() {
    this.fetchLookups();
    this.restoreDraft();
    this.loadLastTxnFromStorage();
  }

  // utils
  validDate() { return dayjs(this.state.trxDate, 'YYYY-MM-DD', true).isValid(); }
  isTypeIN() {
    var current = Number(this.state.tType);
    var dtList = this.state.docTypes || [];
    if (dtList.length) {
      for (var i = 0; i < dtList.length; i++) {
        var dt = dtList[i];
        if (Number(dt.iref) === current) return dt.isNegative === false;
      }
    }
    return current === FALLBACK_DOC_TYPE_IN_ID;
  }
  getLastClosedDateForLoc() {
    var map = this.state.lastCloseByLocation || {};
    var iso = map[this.state.location];
    return iso ? dayjs(iso) : null;
  }
  isBeforeClose() {
    var d = this.getLastClosedDateForLoc();
    if (!d || !this.state.trxDate) return false;
    return dayjs(this.state.trxDate).isBefore(d, 'day');
  }
  nameForProduct(val) {
    var key = String(val || '');
    var map = this.state.productMap || {};
    return map[key] || key;
  }

  lineIsValid(line, isIn) {
    if (!line) return false;
    if (!line.product) return false;
    var q = Number(line.qty);
    if (!isFinite(q) || q === 0) return false;
    if (isIn) {
      var p = Number(line.price);
      if (!isFinite(p) || p <= 0) return false;
    }
    return true;
  }
  canSave() {
    var s = this.state;
    if (!s.location) return false;
    if (!this.validDate()) return false;
    var isIn = this.isTypeIN();
    var t = Number(s.tType);
    if (!(t === Number(s.inId) || t === Number(s.outId))) return false;
    if (!Array.isArray(s.lines) || s.lines.length === 0) return false;
    for (var i = 0; i < s.lines.length; i++) if (!this.lineIsValid(s.lines[i], isIn)) return false;
    return true;
  }

  // lines
  addLine() {
    var next = this.state.lines.slice(0);
    next.push({ product: '', qty: 1, price: '' });
    this.setState({ lines: next }, () => {
      this.saveDraft();

      // Scroll to bottom of lines container
      const container = document.querySelector('.lines-scroll-container');
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  }
  removeLine(index) {
    var next = this.state.lines.slice(0);
    if (index >= 0 && index < next.length) {
      var removed = next.splice(index, 1)[0];
      this.setState({
        lines: next,
        lastDeletedLine: removed,
        lastDeletedIndex: index,
        snackOpen: true,
        snackMsg: 'Line removed',
        snackSeverity: 'info',
      }, this.saveDraft);
    }
  }
  updateLine(index, field, value) {
    var next = this.state.lines.slice(0);
    if (index >= 0 && index < next.length) {
      var row = Object.assign({}, next[index]);
      row[field] = value;
      next[index] = row;
      this.setState({ lines: next }, this.saveDraft);
    }
  }

  fetchLookups() {
    this.setState({ loading: true });

    var locationsResp = this.state.client.getData(
      Config.API_URL_LOCATIONS + 'list_locations/',
      { status: 1 }
    ) || {};
    var productsResp = this.state.client.getData(
      Config.API_URL_PRODUCT + 'list_products/',
      { status: 2, isPurchased: 1, orderBy_name: true }
    ) || {};

    // doc types -> keep only iref 1 & 2
    var docTypesResp = [];
    try {
      var dtr = this.state.client.getData(
        Config.API_URL_REPORTS_TRANSACTIONS + 'doc_types/',
        {}
      );
      if (Array.isArray(dtr)) docTypesResp = dtr;
      else if (dtr && Array.isArray(dtr.results)) docTypesResp = dtr.results;
    } catch (e) {}

    // last month close
    var closeResp = {};
    try {
      var API_BASE = (Config.API_URL_REPORTS_MONTH_CLOSE || '/api/reports/month-close/');
      closeResp = this.state.client.getData(
        API_BASE + 'locations_last_close/',
        {}
      ) || {};
    } catch (e) {}

    var locations = Array.isArray(locationsResp.locations) ? locationsResp.locations.slice(0) : [];
    locations.sort(function(a, b){
      var an = (a && a.name) ? a.name : '';
      var bn = (b && b.name) ? b.name : '';
      return an.localeCompare(bn);
    });
    var products = Array.isArray(productsResp.products) ? productsResp.products.slice(0) : [];

    var filteredDocTypes = [];
    var inId = FALLBACK_DOC_TYPE_IN_ID;
    var outId = FALLBACK_DOC_TYPE_OUT_ID;
    for (var i = 0; i < docTypesResp.length; i++) {
      var dt = docTypesResp[i];
      var iref = Number(dt && dt.iref);
      if (iref === 1 || iref === 2) {
        filteredDocTypes.push(dt);
        if (dt.isNegative === false) inId = iref;
        if (dt.isNegative === true) outId = iref;
      }
    }
    var tType = outId || inId;

    var lastCloseByLocation = {};
    var closeArr = Array.isArray(closeResp) ? closeResp : (Array.isArray(closeResp.results) ? closeResp.results : []);
    for (var j = 0; j < closeArr.length; j++) {
      var r = closeArr[j];
      if (r && r.location_id) lastCloseByLocation[String(r.location_id)] = r.last_close_month || null;
    }

    var productMap = {};
    var productCodeMap = {};
    for (var k = 0; k < products.length; k++) {
      var p = products[k] || {};
      var key = String(p.iref || p.uid || p.id || p.code || '');
      productMap[key] = p.name || p.productName || p.description || key;
      productCodeMap[key] = p.code || p.sku || '';

    }

    this.setState({
      locations: locations,
      products: products,
      productMap: productMap,
      productCodeMap: productCodeMap,
      docTypes: filteredDocTypes,
      inId: inId,
      outId: outId,
      tType: tType,
      lastCloseByLocation: lastCloseByLocation,
      loading: false
    });
  }

  buildPayload() {
    var s = this.state;
    var isIn = this.isTypeIN();

    var docTypeId = Number(s.tType);
    if (!(docTypeId === Number(s.inId) || docTypeId === Number(s.outId))) {
      docTypeId = Number(s.outId);
    }
    if (!isFinite(docTypeId)) return null;

    var linesPayload = [];
    for (var i = 0; i < s.lines.length; i++) {
      var ln = s.lines[i];
      var q = Number(ln.qty);
      var normalizedQty = isIn ? Math.abs(q) : -Math.abs(q);
      linesPayload.push({
        product: String(ln.product),
        quantity: Number(isFinite(normalizedQty) ? normalizedQty.toFixed(4) : 0),
        price: isIn ? Number(Number(ln.price).toFixed(4)) : 0,
        finishedProduct: ''
      });
    }

    return {
      date: s.trxDate,
      location: String(s.location),
      docType: Number(docTypeId),
      docType_id: Number(docTypeId),
      transferFrom: '',
      transferTo: '',
      inventoryTransactionsLines_invTransaction: linesPayload
    };
  }

  postTransaction() {
    var payload = this.buildPayload();
    if (!payload) {
      this.setState({ snackOpen: true, snackMsg: 'Please select a valid transaction type (IN or OUT).', snackSeverity: 'error' });
      return;
    }

    var resp = this.state.client.pushData(
      Config.API_URL_REPORTS_TRANSACTIONS,
      payload
    );

    if (!resp || resp.error) {
      var detail = (resp && (resp.detail || resp.error)) ? (resp.detail || resp.error) : 'Failed to save transaction';
      this.setState({ snackOpen: true, snackMsg: detail, snackSeverity: 'error' });
      return;
    }

    // save to storage for view mode
    try { localStorage.setItem(LAST_TXN_KEY, JSON.stringify(resp)); } catch(e) {}

    this.setState({
      snackOpen: true,
      snackMsg: 'Transaction saved.',
      snackSeverity: 'success',
      lastSaved: resp,
      lines: [ { product: '', qty: 1, price: '' } ],
      mode: 'view'
    }, () => {
      this.clearDraft();
      this.loadLastTxnFromStorage();
    });
  }

  onSave() {
    if (!this.canSave()) return;
    if (this.isBeforeClose()) {
      this.setState({ warningDialogOpen: true, pendingSubmit: this.postTransaction });
      return;
    }
    this.postTransaction();
  }

  duplicateFromSaved() {
    var s = this.state;
    var saved = s.lastSaved || this.loadLastTxnFromStorage() || {};
    var savedLines = [];
    var arr = saved.inventoryTransactionsLines_invTransaction;
    if (Array.isArray(arr)) {
      for (var i = 0; i < arr.length; i++) {
        var ln = arr[i] || {};
        savedLines.push({ product: String(ln.product || ''), qty: Number(ln.quantity || 0), price: Number(ln.price || 0) });
      }
    }
    this.setState({
      location: String(saved.location || s.location),
      trxDate: saved.date ? String(saved.date).slice(0,10) : s.trxDate,
      lines: savedLines.length ? savedLines : s.lines,
      mode: 'edit'
    }, this.saveDraft);
  }

  clearForm() {
    this.setState({ lines: [ { product: '', qty: 1, price: '' } ] }, this.saveDraft);
  }

  findLocationName(iref) {
    var list = this.state.locations || [];
    for (var i = 0; i < list.length; i++) {
      var l = list[i];
      var id = String(l.iref || l.uid || l.id || '');
      if (id === String(iref)) return l.name || l.description || id;
    }
    return String(iref || '');
  }

  saveDraft() {
    try {
      const { location, trxDate, tType, lines } = this.state;
      const draft = { location, trxDate, tType, lines };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    } catch(e) {}
  }
  restoreDraft() {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft) return;
      this.setState({
        location: draft.location || this.state.location,
        trxDate: draft.trxDate || this.state.trxDate,
        tType: Number.isFinite(Number(draft.tType)) ? Number(draft.tType) : this.state.tType,
        lines: Array.isArray(draft.lines) && draft.lines.length ? draft.lines : this.state.lines,
        snackOpen: true,
        snackMsg: 'Draft restored',
        snackSeverity: 'info'
      });
    } catch(e) {}
  }
  clearDraft() { try { localStorage.removeItem(DRAFT_KEY); } catch(e) {} }
  loadLastTxnFromStorage() {
    try {
      const raw = localStorage.getItem(LAST_TXN_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (obj) this.setState({ lastSaved: obj });
      return obj;
    } catch(e) { return null; }
  }

  switchToEdit() { this.setState({ mode: 'edit' }); }
  switchToView() { this.setState({ mode: 'view' }); }

  // --- UI blocks
  renderTitle() {
    return (
      <Box
        sx={{
          flexShrink: 0,
          px: {
            xs: 1,
            sm: 1.5,
          },
          pt: {
            xs: 1,
            sm: 1.25,
          },
          pb: 0.75,
        }}
      >
        <Typography variant="h6">Inventory Internal Transactions</Typography>
        <Typography variant="body2" color="text.secondary">
          Review the last transaction below. Click <b>Add new transaction</b> to create another. After saving, you'll return to this view.
        </Typography>
      </Box>
    );
  }

  renderTopToolbar() {
    var locations = this.state.locations || [];
    var docTypes = this.state.docTypes || [];
    var typeOptions = (docTypes && docTypes.length)
      ? docTypes.map(function(dt){ return { value: Number(dt.iref), label: dt.type || ('Type ' + dt.iref) }; })
      : TYPES_FALLBACK;

    var lastClose = this.getLastClosedDateForLoc();

    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="wrap"
        sx={{
          flexShrink: 0,
          p: {
            xs: 0.75,
            sm: 1,
          },
          '& .MuiIconButton-root': {
            width: 32,
            height: 32,
          },
        }}
      >
        <TextField
          select label="Location" size="small"
          value={this.state.location}
          onChange={(e) => this.setState({ location: e.target.value }, this.saveDraft)}
          sx={{
            ...compactFieldSx,
            width: {
              xs: '100%',
              sm: 220,
            },
          }}
        >
          <MenuItem value="">Select…</MenuItem>
          {locations.map(function(loc){
            var key = String(loc.iref || loc.uid || loc.id || '');
            var val = String(loc.iref || loc.uid || loc.id || '');
            var label = loc.name || loc.description || val;
            return <MenuItem key={key} value={val}>{label}</MenuItem>;
          })}
        </TextField>

        <TextField
          type="date" label="Date" size="small"
          value={this.state.trxDate}
          onChange={(e) => this.setState({ trxDate: e.target.value }, this.saveDraft)}
          sx={{
            ...compactFieldSx,
            width: {
              xs: 'calc(50% - 6px)',
              sm: 150,
            },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          select label="Type" size="small"
          value={this.state.tType}
          onChange={(e) => this.setState({ tType: Number(e.target.value) }, this.saveDraft)}
          sx={{
            ...compactFieldSx,
            width: {
              xs: 'calc(50% - 6px)',
              sm: 260,
            },
          }}
        >
          {typeOptions.map(function(t){ return <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>; })}
        </TextField>

        {lastClose && (
          <Chip size="small" variant="outlined" label={"Last close: " + lastClose.format('YYYY-MM-DD')} />
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Clear form lines">
          <IconButton onClick={this.clearForm} size="small"><ReplayIcon fontSize="small" /></IconButton>
        </Tooltip>
        {this.state.lastSaved && (
          <Tooltip title="Duplicate last saved">
            <IconButton onClick={this.duplicateFromSaved} size="small"><ContentCopyIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  }

  renderLines() {
    var products = this.state.products || [];
    var isIn = this.isTypeIN();

    var productOptions = products.map(function(p){
      return {
        value: String((p.iref || p.uid || p.code || p.id || p.product || '')),
        label: (p.name || p.productName || p.description || p.code || '')
      };
    });

    return (
      <Stack
        spacing={0.75}
        sx={{
          px: {
            xs: 0.75,
            sm: 1,
          },
          pb: 1,
        }}
      >
        {this.state.lines.map((ln, idx) => {
          var selectedProduct = null;
          for (var i = 0; i < productOptions.length; i++) {
            if (productOptions[i].value === String(ln.product)) { selectedProduct = productOptions[i]; break; }
          }
          var qtyError = !Number.isFinite(Number(ln.qty)) || Number(ln.qty) <= 0;
          var priceError = isIn && (!Number.isFinite(Number(ln.price)) || Number(ln.price) <= 0);

          return (
            <Stack
              key={'line-' + idx}
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              sx={{
                gap: 0.75,
                mt: 0.75,
                p: {
                  xs: 0.5,
                  sm: 0,
                },
                borderRadius: {
                  xs: '12px',
                  sm: 0,
                },
                bgcolor: {
                  xs: 'rgba(15, 23, 42, 0.025)',
                  sm: 'transparent',
                },
              }}
            >
              <Autocomplete
                size="small"
                sx={{
                  width: {
                    xs: '100%',
                    sm: 360,
                  },
                  minWidth: 0,
                }}
                value={selectedProduct}
                options={productOptions}
                isOptionEqualToValue={function(o, v){ return ((o && o.value) ? o.value : '') === ((v && v.value) ? v.value : ''); }}
                onChange={(e, opt) => this.updateLine(idx, 'product', opt ? opt.value : '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Product #" + (idx + 1)}
                    sx={{
                      ...compactFieldSx,
                      width: {
                        xs: '100%',
                        sm: 360,
                      },
                    }}
                    error={!ln.product}
                  />
                )}
              />

              <TextField
                type="number" label="Qty" size="small" value={ln.qty}
                // helperText={qtyError ? '>' : ''}
                onChange={(e) => {
                  var raw = String(e.target.value || '').replace(',', '.');
                  if (raw === '') { this.updateLine(idx, 'qty', ''); return; }
                  var n = Number(raw);
                  if (!Number.isFinite(n)) return;
                  if (n < 0) n = Math.abs(n);
                  this.updateLine(idx, 'qty', String(n));
                }}
                onBlur={(e) => {
                  var raw = String(e.target.value || '').replace(',', '.');
                  var n = Number(raw);
                  this.updateLine(idx, 'qty', Number.isFinite(n) && n > 0 ? String(n) : '');
                }}
                onKeyDown={(e) => { if (['e','E','+','-'].indexOf(e.key) >= 0) e.preventDefault(); }}
                onWheel={(e) => e.target.blur()}
                sx={{
                  ...compactFieldSx,
                  width: {
                    xs: 'calc(50% - 28px)',
                    sm: 100,
                  },
                }}
                inputProps={QUANTITY_INPUT_PROPS} error={qtyError}
              />

              {isIn && (
                <TextField
                  type="number" label="Price" size="small" value={ln.price}
                  onChange={(e) => this.updateLine(idx, 'price', e.target.value)}
                  sx={{
                    ...compactFieldSx,
                    width: {
                      xs: 'calc(50% - 28px)',
                      sm: 130,
                    },
                  }}
                  inputProps={PRICE_INPUT_PROPS} error={priceError}
                />
              )}

              <Tooltip title="Remove line">
                <IconButton onClick={() => this.removeLine(idx)} size="small"><DeleteIcon fontSize="small" /></IconButton>
              </Tooltip>
            </Stack>
          );
        })}

        <Button 
          onClick={this.addLine} startIcon={<AddIcon />} size="small" variant="outlined" 
          sx={{
            ...compactButtonSx,
            alignSelf: 'flex-start',
            mt: 0.25,
          }}
        >
          Add line
        </Button>
      </Stack>
    );
  }

  renderSaveBar() {
    const isIn = this.isTypeIN();
    let totalIN = 0;
    if (isIn) {
      for (let i = 0; i < this.state.lines.length; i++) {
        const ln = this.state.lines[i];
        const q = Number(ln.qty || 0);
        const p = Number(ln.price || 0);
        if (isFinite(q) && isFinite(p)) totalIN += Math.abs(q) * p;
      }
    }

    return (
      <Box sx={compactSaveBarSx}>
        <Chip size="small" label={this.isTypeIN() ? 'IN' : 'OUT'} color={this.isTypeIN() ? 'primary' : 'default'} />
        {isIn && <Chip size="small" color="success" label={`Total: ${money(totalIN)}`} />}
        <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>
          {this.state.lines.length} line{this.state.lines.length !== 1 ? 's' : ''}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* 👇 Cancel Edit button */}
        <Button
          variant="outlined"
          color="inherit"
          size="large"
          onClick={() => {
            if (window.confirm('Discard unsaved changes?')) {
              this.clearDraft();
              this.switchToView();
            }
          }}
          sx={{
            px: {
              xs: 1.25,
              sm: 2,
            },
            flex: {
              xs: '1 1 auto',
              sm: '0 0 auto',
            },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={this.onSave}
          disabled={!this.canSave() || this.state.saving}
          sx={{
            px: {
              xs: 1.5,
              sm: 3,
            },
            fontWeight: 750,
            flex: {
              xs: '1 1 auto',
              sm: '0 0 auto',
            },
          }}
        >
          Save
        </Button>
      </Box>
    );
  }

  renderViewMode() {
    var saved = this.state.lastSaved;
    return (
      <Paper elevation={1} sx={mainCardSx}>
        {this.renderTitle()}
        <Divider />
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            p: {
              xs: 1,
              sm: 1.5,
            },
          }}
        >
          {saved ? (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Last saved transaction</Typography>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <b>ID:</b> {saved.iref ? '#' + saved.iref : '—'}&nbsp;&nbsp;•&nbsp;&nbsp;
                <b>Date:</b> {saved.date ? String(saved.date).slice(0,10) : '—'}&nbsp;&nbsp;•&nbsp;&nbsp;
                <b>Location:</b> {this.findLocationName(saved.location)}&nbsp;&nbsp;•&nbsp;&nbsp;
                <b>Type:</b> {this.isTypeIN() ? 'IN' : 'OUT'}
              </Typography>
              <Divider sx={{ my: 1, mb: 2 }} />
              {(Array.isArray(saved.inventoryTransactionsLines_invTransaction) && saved.inventoryTransactionsLines_invTransaction.length) ? (
                <Stack spacing={0.5} sx={{ mb: 1.5, mt: 1.5 }}>
                  {saved.inventoryTransactionsLines_invTransaction.map((ln, i) => (
                    <Typography key={'sv-ln-'+i} variant="body2">
                      {i+1}. {this.nameForProduct(ln.product)} — Qty: {Number(ln.quantity || 0)}{!ln.price ? '' : (', Price: ' + money(ln.price))}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No lines.</Typography>
              )}
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>No previous transaction found.</Alert>
          )}

          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flexWrap: 'wrap',
              '& .MuiButton-root': compactButtonSx,
            }}
          >
            <Button variant="contained" onClick={this.switchToEdit}>Add new transaction</Button>
            {saved && (
              <Button variant="outlined" onClick={this.duplicateFromSaved}>Duplicate last transaction</Button>
            )}
          </Stack>
        </Box>
      </Paper>
    );
  }

  renderEditMode() {
    return (
      <Paper elevation={1} sx={mainCardSx}>
        {/* Title */}
        {this.renderTitle()}
        <Divider />

        {/* Compact header toolbar */}
        {this.renderTopToolbar()}
        <Divider />

        {/* Scrollable lines */}
        <Box
          className="lines-scroll-container"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
          }}
        >
          {this.isBeforeClose() && (
            <Alert
              sx={{
                m: {
                  xs: 0.75,
                  sm: 1,
                },
                py: {
                  xs: 0.25,
                  sm: 0.5,
                },
                fontSize: {
                  xs: '0.76rem',
                  sm: '0.84rem',
                },
              }}
              severity="warning"
              icon={<WarningAmber />}
            >
              Selected date is before last month close for this location
              {this.getLastClosedDateForLoc()
                ? ' (' + this.getLastClosedDateForLoc().format('YYYY-MM-DD') + ')'
                : ''}
              .
            </Alert>
          )}
          {this.renderLines()}
        </Box>

        {/* Sticky save/footer bar */}
        {this.renderSaveBar()}

        {this.state.loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>
    );
  }

  render() {
    return (
      <Box sx={pageRootSx}>
        {this.state.mode === 'view' ? this.renderViewMode() : this.renderEditMode()}

        <Dialog
          open={this.state.warningDialogOpen}
          onClose={() => {}}
          disableEscapeKeyDown
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Proceed with back-dated transaction?</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              You are adding a transaction dated <b>{this.state.trxDate}</b> which is before the last close for the selected location.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ warningDialogOpen: false, pendingSubmit: null })}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                var fn = this.state.pendingSubmit;
                this.setState(
                  { warningDialogOpen: false, pendingSubmit: null },
                  () => { if (fn) fn(); }
                );
              }}
            >
              Proceed
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={this.state.snackOpen}
          autoHideDuration={4000}
          onClose={() => this.setState({ snackOpen: false })}
          message={this.state.snackMsg}
        />
      </Box>
    );
  }
}

export default InventoryInternalTransactionsSmartCompactV2;